// ---------------------------------------------------------------------------
// A stored round, read as a distribution.
//
// The record page draws a round as a table of people above `LARGE_ROOM_FROM`
// seats and as a distribution at or above it, which is the same line the live
// reveal switches on (`revealIsAggregated`). One number, so a game that played
// as an audience is remembered as one.
//
// --- Why this is not `buildRoundAggregate` ---------------------------------
//
// The engine has an aggregate builder and this is deliberately not a call to it.
// It takes a `RoundState`, and a `GameRecordRoundView` cannot be turned into one
// without inventing things — a clock, the tile piles, an `actionId` per answer —
// which is the rule at the top of `GameRecordScreen.tsx`: a record that invents
// things is worse than a smaller record.
//
// **The decisive missing field is the one the record removes on purpose.**
// `revealOrder` sorts on `lockedAt`, and the public record carries no timestamp
// on an answer at all (DESIGN sections 5 and 11 — logged, never enforced, no
// accusations). So a reconstructed round would tie-break on a value that is not
// there, and would produce an order that looks like the reveal's and is not it.
// This orders by what IS stored — score, then declared length, then the answer
// itself — which is deterministic and says so.
//
// Everything that decides what the reader sees is shared with the live screen
// rather than re-decided: `AGGREGATE_ROWS`, `AGGREGATE_NAMES_SHOWN`, ranking by
// SCORE and not by popularity, and the wording of the footer line, which comes
// out of the reveal's own `aggregate.ts`.
//
// --- What this must never change -------------------------------------------
//
// **Every word this puts on the screen has to stay definable.** The membership
// set for `GET /api/games/:id/rounds/:i/definition` is
// `revealedWordsOfRecordRound` in `packages/backend/src/definitions.ts`, and it
// admits every submission's answer, the conundrum's solution, the AI shadow
// answers and the best answer with its alternatives. Compression only ever shows
// FEWER of those, never a word from somewhere else, so the set does not move —
// and it must not be tightened to match what is drawn, because the LIVE round
// still admits every submission and CLAUDE.md's rule is that the two admit the
// same set. A word definable but not drawn is safe: it was genuinely played and
// genuinely revealed in that round, which is the whole of what the gate asks.
// `gameRecordAggregate.test.ts` pins that no row here carries a word that was
// not a submission.
// ---------------------------------------------------------------------------

import {
  AGGREGATE_NAMES_SHOWN,
  AGGREGATE_ROWS,
  LARGE_ROOM_FROM,
  stepsToExpression,
} from '@countdown/shared';
import type {
  AggregateEntry,
  GameRecordResponse,
  GameRecordRoundView,
  GameRecordSubmissionView,
  RoundAggregate,
} from '@countdown/shared';
import { listNames } from '../lib/format.js';

/**
 * Whether this game's rounds are drawn as distributions.
 *
 * Seat count against `LARGE_ROOM_FROM`, and nothing else. The stored record
 * carries no settings, so a room that forced `aggregateReveal: 'on'` at four
 * players still gets the ordinary table here — which is the right way for that
 * to fail: four rows are readable either way, and the compression exists for the
 * size where they are not.
 */
export function recordIsAggregated(record: GameRecordResponse): boolean {
  return record.players.length >= LARGE_ROOM_FROM;
}

/** A host ruling wins in both directions; otherwise judging did. */
function stands(answer: GameRecordSubmissionView): boolean {
  if (answer.hostOverride === 'allow') return true;
  if (answer.hostOverride === 'reject') return false;
  return answer.valid !== false;
}

/**
 * An answer with nothing in it, which is a seat that sat the round out rather
 * than a seat that got it wrong.
 *
 * Mirrors the engine's `submittedNothing`: a null declared length is nothing
 * whatever else the row says, an empty word is nothing, and a numbers answer
 * with no value is nothing.
 */
function playedNothing(answer: GameRecordSubmissionView): boolean {
  if (answer.declaredLength === null) return true;
  if (answer.kind === 'numbers') return answer.value === null;
  return (answer.answer ?? '').trim().length === 0;
}

/**
 * How one stored answer is grouped, and how it will be drawn.
 *
 * The engine's `aggregateKeyOf`, against the columns the record kept. Numbers
 * group on the VALUE rather than on the working, because two people who both
 * reached 723 have played the same answer as far as this screen is concerned and
 * almost nobody reaches it the same way — grouping on the expression is two
 * hundred rows of one, which is the uncompressed page with extra steps.
 */
function keyOf(
  round: GameRecordRoundView,
  answer: GameRecordSubmissionView,
): { key: string; text: string } {
  if (answer.kind === 'numbers') {
    const steps = answer.steps ?? [];
    const text =
      steps.length > 0
        ? stepsToExpression(round.numbersTiles ?? [], steps)
        : String(answer.value ?? '');
    return { key: `n:${answer.value ?? 'none'}`, text };
  }
  const word = answer.answer ?? '';
  return { key: `${answer.kind === 'letters' ? 'w' : 'c'}:${word}`, text: word };
}

/** Ascending, contiguous, and a bucket with nothing in it is still a bucket. */
function bucketsOf(values: readonly number[]): { value: number; count: number }[] {
  if (values.length === 0) return [];
  const counts = new Map<number, number>();
  let low = Infinity;
  let high = -Infinity;
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
    if (value < low) low = value;
    if (value > high) high = value;
  }
  const out: { value: number; count: number }[] = [];
  for (let value = Math.min(0, low); value <= high; value += 1) {
    out.push({ value, count: counts.get(value) ?? 0 });
  }
  return out;
}

/** Capped one past the last scoring band, as the engine's histogram is. */
const FAR = 11;

function distanceBucket(
  round: GameRecordRoundView,
  answer: GameRecordSubmissionView,
): number | null {
  if (round.numbersTarget === null || answer.value === null) return null;
  return Math.min(FAR, Math.abs(answer.value - round.numbersTarget));
}

/**
 * The order answers are walked in, which decides which two players a row names.
 *
 * Worst first, so a row's named players are the ones the reveal would have
 * reached first, and the record's own answer table is already sorted this way —
 * see `Round` in `GameRecordScreen.tsx`. The third key is the player id, which
 * is not a tie-break anybody reads: it is there so the same stored game produces
 * the same page twice, which `Array.prototype.sort` does not otherwise promise
 * across engines.
 */
function worstFirst(
  a: GameRecordSubmissionView,
  b: GameRecordSubmissionView,
): number {
  return (
    (a.score ?? 0) - (b.score ?? 0) ||
    (a.declaredLength ?? 0) - (b.declaredLength ?? 0) ||
    (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0)
  );
}

/**
 * One stored round as a distribution.
 *
 * `seatCount` is every seat the game had, which is what `noAnswer` is counted
 * against: a seat that put up nothing has no submission row at all, so counting
 * it needs the roster rather than the answers.
 *
 * Null for a round that was never judged. A game ended early keeps the round it
 * was ended in, started and unrevealed, and there is no distribution of a round
 * nobody was shown.
 */
export function aggregateRecordRound(
  round: GameRecordRoundView,
  seatCount: number,
): RoundAggregate | null {
  if (round.revealedAt === null) return null;

  interface Row {
    answer: string;
    score: number;
    valid: boolean;
    playerIds: string[];
    count: number;
  }
  const rows = new Map<string, Row>();

  let noAnswer = 0;
  const answerValues: number[] = [];
  const scores: number[] = [];

  for (const answer of [...round.submissions].sort(worstFirst)) {
    const score = answer.score ?? 0;
    scores.push(score);

    if (playedNothing(answer)) {
      noAnswer += 1;
      continue;
    }

    if (round.type === 'numbers') {
      const bucket = distanceBucket(round, answer);
      if (bucket !== null) answerValues.push(bucket);
    } else {
      answerValues.push(answer.declaredLength ?? 0);
    }

    const { key, text } = keyOf(round, answer);
    const valid = stands(answer);
    const existing = rows.get(`${key}|${valid}`);
    if (existing === undefined) {
      rows.set(`${key}|${valid}`, {
        answer: text,
        score,
        valid,
        playerIds: [answer.playerId],
        count: 1,
      });
    } else {
      existing.count += 1;
      if (existing.playerIds.length < AGGREGATE_NAMES_SHOWN) {
        existing.playerIds.push(answer.playerId);
      }
    }
  }

  // Seats with no row of their own. A game may have fewer answers than seats for
  // two reasons that are one fact here — somebody sat the round out, or somebody
  // joined after it — and both are a seat this round has nothing to say about.
  const silent = Math.max(0, seatCount - round.submissions.length);
  noAnswer += silent;
  for (let index = 0; index < silent; index += 1) scores.push(0);

  // **Ranked by score, never by popularity.** The word forty people found is
  // usually a short one, so ranking by count puts the round's best answer below
  // its most obvious one. Popularity is still on every row, as `count`.
  const ranked = [...rows.values()].sort(
    (a, b) =>
      b.score - a.score ||
      b.count - a.count ||
      (a.answer < b.answer ? -1 : a.answer > b.answer ? 1 : 0),
  );

  const top: AggregateEntry[] = ranked.slice(0, AGGREGATE_ROWS).map((row) => ({
    answer: row.answer,
    score: row.score,
    valid: row.valid,
    playerIds: [...row.playerIds],
    count: row.count,
  }));
  const rest = ranked.slice(AGGREGATE_ROWS);

  return {
    top,
    otherAnswers: rest.length,
    otherPlayers: rest.reduce((sum, row) => sum + row.count, 0),
    noAnswer,
    // Built, and not drawn on this page. The reveal has room for two charts a
    // round and a fifteen-round record does not — the whole redesign of this
    // page was 9,606px down to 3,432 — so what accounts for the rest of the room
    // here is the footer line, in text. They are computed anyway because they are
    // ten lines and because leaving them out would make this something other than
    // a `RoundAggregate`, which is the type the live screen's readers take.
    histogram: bucketsOf(answerValues),
    scoreHistogram: bucketsOf(scores),
  };
}

/**
 * Who found an answer, in the words the row says out loud.
 *
 * The reveal's `findersLine` reads a `ClientGameState` and there is no such
 * thing here, so this takes the seat lookup the record page already builds. The
 * grammar is the same on purpose: `ALICE, BOB and 12 others`.
 *
 * A named id that resolves to no seat is dropped from the names and stays inside
 * `count` — the row can still say how many found the word even when it cannot
 * say what one of them was called.
 */
export function recordFindersLine(
  entry: AggregateEntry,
  nameOf: (playerId: string) => string | null,
): string {
  const named = entry.playerIds
    .slice(0, AGGREGATE_NAMES_SHOWN)
    .map(nameOf)
    .filter((name): name is string => name !== null);

  const others = Math.max(0, entry.count - named.length);
  if (named.length === 0) return others === 1 ? '1 player' : `${others} players`;
  if (others === 0) return listNames(named);
  // `named.join(', ')` and not `listNames`, matching `findersLine` in
  // `reveal/aggregate.ts` — read the comment there, which is the argument.
  // `listNames` puts an `and` between two names, so with a tail on the end the
  // line comes out as `Alice and Bob and 12 others` and the first `and` reads as
  // joining Bob to the count rather than to the list. This said `listNames` and
  // the reveal said `join`, which is the live screen and the permanent page
  // disagreeing about the same sentence — the one failure importing the rest of
  // this module's wording from the reveal exists to prevent.
  return `${named.join(', ')} and ${others === 1 ? '1 other' : `${others} others`}`;
}
